import {
  $convertFromMarkdownString,
  type TextMatchTransformer,
  TRANSFORMERS,
} from "@lexical/markdown";
import { $restoreEditorState } from "@lexical/utils";
import { $getNodeByKey, type LexicalEditor, type LexicalNode } from "lexical";
import { isEmpty } from "lodash-es";
import { getAspectRatio } from "../../../../utils/aspect-ratio";
import { $createDesignHuddleNodeFromAsset } from "../nodes/design-huddle-mui";
import { $isDesignHuddleNode } from "../nodes/design-huddle-node";
import { $isImageNode } from "../nodes/image-node";
import { $createImageNode } from "../nodes/image-node-mui";
import type { SerialisedEditorDesign } from "../types/serialised-editor-design";

const isMarkdown = (
  design: SerialisedEditorDesign | undefined,
): design is { type: "markdown"; markdown: string } =>
  design !== undefined && "type" in design && design?.type === "markdown";

const HUDDLE: TextMatchTransformer = {
  export: (node: LexicalNode) => {
    if (!$isDesignHuddleNode(node)) {
      return null;
    }

    return `![](${node.__url})`;
  },
  importRegExp:
    /!(?:\[[^[]*)\]\((https:\/\/images[^.]+\.changeengine.com\/([^/]+)\/[^/]+\/[^/]+\/[^/]+\?p=([^)]+))\)/,
  regExp:
    /!(?:\[([^[]*)\])\((https:\/\/images[^.]+\.changeengine.com\/([^/]+)\/[^/]+\/[^/]+\/[^/]+\?p=([^)]+))\)/,
  replace: (textNode, match) => {
    const [, preview, account_id, project_id] = match;
    const imageNode = $createDesignHuddleNodeFromAsset(
      false,
      account_id!,
      project_id,
      1,
      1,
      undefined,
      preview,
    );
    textNode.replace(imageNode);
  },
  trigger: ")",
  type: "text-match",
  dependencies: [],
};

const imageTransformer = (editor: LexicalEditor): TextMatchTransformer => ({
  export: (node: LexicalNode) => {
    if (!$isImageNode(node)) {
      return null;
    }

    return `![](${node.getHref()})`;
  },
  importRegExp: /!(?:\[([^[]*)\])(?:\(([^(]+)\))/,
  regExp: /!(?:\[([^[]*)\])(?:\(([^(]+)\))$/,
  replace: (textNode, match) => {
    const [, , src] = match;
    const imageNode = $createImageNode(false, src!, 1, 1);
    textNode.replace(imageNode);

    const nodeKey = imageNode.getKey();
    void getAspectRatio(src!)
      .then((aspect) => {
        editor.update(() => {
          const node = $getNodeByKey(nodeKey);
          if ($isImageNode(node) && node.__url === src) {
            node.setSize(node.__proportionOfMaxWidth, aspect);
          }
        });
      })
      .catch(() => undefined);
  },
  trigger: ")",
  type: "text-match",
  dependencies: [],
});

export const $loadDesign = (
  design: SerialisedEditorDesign | undefined,
): ((e: LexicalEditor) => void) | undefined =>
  isEmpty(design)
    ? undefined
    : isMarkdown(design)
      ? (e) =>
          $convertFromMarkdownString(design.markdown, [
            HUDDLE,
            imageTransformer(e),
            ...TRANSFORMERS,
          ])
      : (e) => $restoreEditorState(e, e.parseEditorState(design));
